import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';
import { threadSize, type ThreadComment } from '../../community/ui/CommentThread';
import { toast } from '../../community/ui/toast';
import { invalidateCatalogue } from '../../community/api/freshness';

/**
 * Comments: the client for the server's comments.ts, which both editions
 * mount (reading follows reading the thing, writing its workspace role). The
 * viewer's Discussion reads it in either shell; the community pages read it
 * for collections too.
 */
export type TargetKind = 'collection' | 'study';

export interface CommentRow {
  id: string;
  authorId: string;
  name: string;
  isYou: boolean;
  body: string;
  deleted: boolean;
  createdUtc: string;
  editedUtc: string | null;
  replies?: CommentRow[];
}

class ApiError extends HttpError {}

export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiRoot}${path}`, { credentials: 'include', headers: init?.body ? { 'content-type': 'application/json' } : {}, ...init });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
  return body as T;
}

const seg = encodeURIComponent;

/** "2 d ago", "Just now": the prototype's relative times. */
export function relativeTime(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} w ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function toThread(rows: CommentRow[]): ThreadComment[] {
  const one = (c: CommentRow): ThreadComment => ({ id: c.id, name: c.name, isYou: c.isYou, text: c.deleted ? 'This comment was deleted.' : c.body, when: relativeTime(c.createdUtc), edited: !!c.editedUtc, replies: (c.replies ?? []).map(one) });
  return rows.map(one);
}

/** The thread's size, counted where the thread is drawn (CommentThread), for the dock's header and the rail. */
export { threadSize };

export function useComments(kind: TargetKind, id: string | undefined) {
  return useQuery({
    queryKey: ['comments', kind, id],
    queryFn: () => call<{ comments: CommentRow[] }>(`/comments/${kind}/${seg(id!)}`),
    enabled: !!id,
  });
}

/**
 * The thread as this person may read it: members through /comments, guests
 * through /public/comments (Public only), and anybody holding a share link
 * through that link's namespace, signed in or not.
 */
export function useThread(kind: TargetKind, id: string | undefined, guest: boolean | undefined, shareId?: string) {
  const prefix = shareId ? `/s/${seg(shareId)}` : guest ? '/public' : '';
  return useQuery({
    queryKey: ['comments', kind, id, shareId ?? guest],
    queryFn: () => call<{ comments: CommentRow[] }>(`${prefix}/comments/${kind}/${seg(id!)}`),
    // Not until the session has answered: the wrong namespace only earns a 401.
    // A share link's namespace answers the same for everyone.
    enabled: !!id && (!!shareId || guest !== undefined),
  });
}

/** Posting, editing and deleting your own comments; through the share link's namespace when given one. */
export function useCommentMutations(kind: TargetKind, id: string | undefined, shareId?: string) {
  const prefix = shareId ? `/s/${seg(shareId)}` : '';
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['comments', kind, id] });
    void invalidateCatalogue(qc);
  };
  const fail = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong');
  return {
    post: useMutation({ mutationFn: (v: { body: string; parentId?: string }) => call(`${prefix}/comments/${kind}/${seg(id!)}`, { method: 'POST', body: JSON.stringify(v) }), onSuccess: done, onError: fail }),
    edit: useMutation({ mutationFn: (v: { commentId: string; body: string }) => call(`${prefix}/comments/${kind}/${seg(id!)}/${seg(v.commentId)}`, { method: 'PATCH', body: JSON.stringify({ body: v.body }) }), onSuccess: done, onError: fail }),
    remove: useMutation({ mutationFn: (commentId: string) => call(`${prefix}/comments/${kind}/${seg(id!)}/${seg(commentId)}`, { method: 'DELETE' }), onSuccess: done, onError: fail }),
  };
}
